// The calculation core of the time-tracking domain, extracted from Time.js so it
// is testable in isolation: employment phases, Soll targets, holiday/vacation/sick
// credits, the vacation balance with carry-over and the hours-account balance.
// Everything here is a PURE function — no React, no fetches, no Date.now() unless
// passed in. UI formatting stays in the components.
import {
  getMonday, addDays, formatISODate, parseISODateLocal, isoAddDays,
  isoWeekday, weekKeyOf,
} from "./dates";

// ---- Entry helpers ------------------------------------------------------------

export function entryDurationMs(entry, nowMs) {
  const start = new Date(entry.started_at).getTime();
  const end = entry.ended_at ? new Date(entry.ended_at).getTime() : nowMs;
  return Math.max(0, end - start);
}

// A public holiday overrides a sick day: a sick entry that falls on a holiday must
// NOT be counted, because the holiday credit already covers that day's target
// (otherwise the day would count twice). Work on a holiday still counts (overtime).
export function holidayDateSet(holidayEntries) {
  const s = new Set();
  for (const h of holidayEntries || []) s.add(formatISODate(new Date(h.started_at)));
  return s;
}
export function countsAsTracked(entry, holidaySet) {
  return !(entry.kind === "sick" && holidaySet.has(formatISODate(new Date(entry.started_at))));
}

// ---- Workday / Soll helpers ---------------------------------------------------
// Parse "1,2,3h,4,5" → {full:Set(int), half:Set(int)}. Days are 1=Mon..7=Sun.
export function parseFixedWorkdays(str) {
  const full = new Set();
  const half = new Set();
  for (const raw of (str || "").split(",").map((s) => s.trim()).filter(Boolean)) {
    const isHalf = /h$/i.test(raw);
    const day = Number(isHalf ? raw.slice(0, -1) : raw);
    if (!Number.isFinite(day) || day < 1 || day > 7) continue;
    if (isHalf) half.add(day); else full.add(day);
  }
  return { full, half };
}

// ---- Employment phases (effective-dated) ------------------------------------
// The active phase for a date, or null. Intervals are half-open [start, end).
// While the migration keeps one phase per user that mirrors the legacy fields,
// these accessors return exactly the legacy values; the `?? user.*` fallback
// covers dates outside any phase, so behaviour is unchanged until phases diverge.
export function phaseAt(user, date) {
  const iso = formatISODate(date);
  for (const p of user?.phases || []) {
    if (p.start_date && iso < p.start_date) continue;
    if (p.end_date && iso >= p.end_date) continue;
    return p;
  }
  return null;
}
export function effFixedWorkdays(user, date) {
  const p = phaseAt(user, date);
  return (p ? p.fixed_workdays : user?.fixed_workdays) || "";
}
export function effTypeAt(user, date) {
  const p = phaseAt(user, date);
  if (p) return p.type;
  return user?.role === "working_student" ? "working_student" : "full_time";
}
export function isWorkingStudentAt(user, date) {
  return effTypeAt(user, date) === "working_student";
}

// Effective weekly workday weight given fixed_workdays (each half day = 0.5).
// When fixed_workdays is empty we default to Mo–Fr full (weight 5) and mark it.
export function dailyWorkPattern(user, date) {
  const { full, half } = parseFixedWorkdays(effFixedWorkdays(user, date));
  if (full.size === 0 && half.size === 0) {
    return { full: new Set([1, 2, 3, 4, 5]), half: new Set(), weight: 5, defaulted: true };
  }
  return { full, half, weight: full.size + 0.5 * half.size, defaulted: false };
}

// Hours-per-week for the given date, honoring per-week overrides.
export function weeklyHoursForDate(user, date, fallback) {
  const overrides = user?.weekly_hours_overrides || {};
  const key = weekKeyOf(date);
  if (overrides[key] != null) return Number(overrides[key]);
  return phaseAt(user, date)?.weekly_hours ?? user?.weekly_hours ?? fallback ?? 40;
}

// Per-month override key for a split week (e.g. "2026-14#2" = KW14, March portion).
export function portionKeyOf(date) {
  return `${weekKeyOf(date)}#${date.getMonth()}`;
}

// Summed workday weight (full=1, half=0.5) of the days in the SAME ISO week that
// fall in the same month+year as `date` — i.e. that week's portion in that month.
function portionWorkdayWeight(user, date) {
  const pat = dailyWorkPattern(user, date);
  const monday = getMonday(date);
  const m = date.getMonth(), y = date.getFullYear();
  let w = 0;
  for (let i = 0; i < 7; i++) {
    const d = addDays(monday, i);
    if (d.getMonth() !== m || d.getFullYear() !== y) continue;
    const wd = isoWeekday(d);
    if (pat.full.has(wd)) w += 1;
    else if (pat.half.has(wd)) w += 0.5;
  }
  return w;
}

// Soll for one weekday (in ms). 0 on non-workdays.
export function dailyTargetMs(user, date) {
  const wd = isoWeekday(date);
  // For users with employment phases, a date outside every phase (before the first,
  // after the last, or in a gap between phases) means "not employed" → no Soll.
  if ((user?.phases?.length) && !phaseAt(user, date)) return 0;
  const pat = dailyWorkPattern(user, date);
  if (pat.weight === 0) return 0;
  const overrides = user?.weekly_hours_overrides || {};
  // A per-month portion override (from a manually-edited split week) is spread
  // ONLY across that portion's own workdays — no re-proration across the week.
  const pKey = portionKeyOf(date);
  if (overrides[pKey] != null) {
    const pw = portionWorkdayWeight(user, date);
    if (pw === 0) return 0;
    const perFull = (Number(overrides[pKey]) * 3600000) / pw;
    if (pat.full.has(wd)) return perFull;
    if (pat.half.has(wd)) return perFull / 2;
    return 0;
  }
  const weeklyH = weeklyHoursForDate(user, date);
  const perFullDay = (weeklyH * 3600000) / pat.weight;
  if (pat.full.has(wd)) return perFullDay;
  if (pat.half.has(wd)) return perFullDay / 2;
  return 0;
}

// How many hours a public holiday credits the account.
// A "flexibel" werkstudent (no fixed_workdays) gets 0 — they have no fixed
// expected workday. Everyone else gets a fraction of their per-day Soll based
// on whether the holiday weekday is a half or full workday for them.
export function holidayCreditMs(user, date) {
  const pat = dailyWorkPattern(user, date);
  if (pat.defaulted && isWorkingStudentAt(user, date)) return 0;
  return dailyTargetMs(user, date);
}

// Vacation credit. Working students take vacation in whole 8h days: they work
// their weekly hours as ~8h chunks on a few days (not a flat 4h across all five),
// so a full vacation day is worth a full 8h working day (half day = 4h),
// independent of the averaged daily Soll. Everyone else credits their daily Soll
// (which for a 5-day full-timer already equals a normal working day).
export function vacationCreditMs(user, date) {
  // Same guard as dailyTargetMs: outside every phase the person isn't employed, so
  // there is nothing to credit — without it a werkstudent would get 8 h/day in a
  // phase gap or for vacation days booked after their phase ended.
  if (user?.phases?.length && !phaseAt(user, date)) return 0;
  if (isWorkingStudentAt(user, date)) return 8 * 3600000;
  return dailyTargetMs(user, date);
}

// Sick-day credit — same per-day Soll as vacation/holiday. A half sick day
// credits half the daily Soll. Replaces the old hard-coded 8h/4h.
export function sickCreditMs(user, date, half = false) {
  const full = dailyTargetMs(user, date);
  return half ? full / 2 : full;
}

// ---- Synthetic credit entries (single source of truth) -----------------------
// Feiertage and approved Urlaub are not stored as time_entries; both the "Zeit"
// tab and the admin overview turn them into synthetic entries whose duration
// equals the per-user credit. Extracted here so every caller stays consistent.
export function buildHolidayEntries(user, holidays) {
  const list = [];
  for (const [dateStr, name] of Object.entries(holidays || {})) {
    const [y, m, d] = dateStr.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    const wd = date.getDay();
    if (wd === 0 || wd === 6) continue;
    const creditMs = user ? holidayCreditMs(user, date) : 8 * 3600000;
    // Number.isFinite statt "> 0": ein NaN-Guthaben (etwa aus einem kaputten
    // Stunden-Override) rutschte durch jeden Vergleich hindurch und ergab ein
    // ungueltiges Datum — toISOString warf dann "Invalid time value" und riss
    // die ganze Oberflaeche mit.
    if (!Number.isFinite(creditMs) || creditMs <= 0) continue;
    const startDate = new Date(`${dateStr}T09:00`);
    if (Number.isNaN(startDate.getTime())) continue;
    const start = startDate.toISOString();
    list.push({
      id: `hol-${dateStr}`,
      started_at: start,
      ended_at: new Date(new Date(start).getTime() + creditMs).toISOString(),
      kind: "holiday",
      description: name,
      synthetic: true,
    });
  }
  return list;
}

export function buildVacationEntries(user, vacations, holidays) {
  const list = [];
  for (const v of vacations || []) {
    if (v.status !== "approved") continue;
    const [sy, sm, sd] = v.start_date.split("-").map(Number);
    const [ey, em, ed] = v.end_date.split("-").map(Number);
    let cur = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    while (cur <= end) {
      const wd = cur.getDay();
      const ds = formatISODate(cur);
      // Skip weekends AND public holidays — holidays credit the day on their own.
      if (wd !== 0 && wd !== 6 && !(holidays || {})[ds]) {
        // Half-day vacation: the whole (single) day, or only the last day of a
        // range (half_end), credits half the day's Soll.
        const factor = (v.half_day || (v.half_end && ds === v.end_date)) ? 0.5 : 1;
        const creditMs = (user ? vacationCreditMs(user, cur) : 8 * 3600000) * factor;
        const startDate = new Date(`${ds}T09:00`);
        if (Number.isFinite(creditMs) && creditMs > 0 && !Number.isNaN(startDate.getTime())) {
          const start = startDate.toISOString();
          list.push({
            id: `vac-${v.id}-${ds}`,
            started_at: start,
            ended_at: new Date(new Date(start).getTime() + creditMs).toISOString(),
            kind: "vacation",
            synthetic: true,
          });
        }
      }
      cur = addDays(cur, 1);
    }
  }
  return list;
}

// ---- Pro-rated entitlement for working students -----------------------------
// A working student's contractual vacation_days assumes a full 5-day week. Their
// actual entitlement is earned pro rata to the hours they work+credit in the
// year, measured against a full-time year: 30 days at full time ⇒
//   entitlement(year) = vacation_days × (worked+credited hours in year ÷ 2080).
// Full-timers keep their flat contractual value.
export const FULLTIME_YEAR_HOURS = 40 * 52; // 2080 — a full 5-day week for a whole year

// Sum of worked + credited hours (ms) whose start falls in `year`. `entries`
// should already include the synthetic holiday/vacation credit entries.
function trackedMsInYear(entries, year, nowMs) {
  let ms = 0;
  for (const e of entries || []) {
    if (new Date(e.started_at).getFullYear() !== year) continue;
    ms += entryDurationMs(e, nowMs);
  }
  return ms;
}
// Same, over a half-open [start, end) date range (both local Date objects).
function trackedMsInRange(entries, start, end, nowMs) {
  let ms = 0;
  for (const e of entries || []) {
    const t = new Date(e.started_at);
    if (t >= start && t < end) ms += entryDurationMs(e, nowMs);
  }
  return ms;
}

// Returns a (year) => entitlement-in-days function.
// Phase-aware: a year's entitlement is the SUM of each employment phase's accrual
// during that year (no carry across phases). Per phase, by its vacation_rule:
//   none        → 0
//   prorated    → vacation_days × (worked+credited hours in year∩phase ÷ 2080)
//   flat        → vacation_days PER YEAR × (share of the year the phase covers)
//   phase_total → vacation_days for the WHOLE phase, split over the years it
//                 touches proportionally to days (the years sum to vacation_days)
// Falls back to the legacy user fields when a user has no phases yet.
// Pass `entries` INCLUDING synthetic holiday/vacation credit (Arbeit + Gutschriften).
export function entitlementForYear(user, entries, nowMs = Date.now()) {
  const phases = user?.phases || [];
  if (phases.length === 0) {
    const base = user?.vacation_days ?? 30;
    if (user?.role !== "working_student") return () => base;
    return (year) => base * (trackedMsInYear(entries, year, nowMs) / 3600000) / FULLTIME_YEAR_HOURS;
  }
  return (year) => {
    const yStart = new Date(year, 0, 1);
    const yEnd = new Date(year + 1, 0, 1); // [yStart, yEnd)
    const yearDays = (yEnd - yStart) / 86400000;
    let total = 0;
    for (const p of phases) {
      const pStart = p.start_date ? parseISODateLocal(p.start_date) : yStart;
      const pEnd = p.end_date ? parseISODateLocal(p.end_date) : yEnd; // exclusive
      const oStart = pStart > yStart ? pStart : yStart;
      const oEnd = pEnd < yEnd ? pEnd : yEnd;
      if (oEnd <= oStart) continue; // phase doesn't touch this year
      const rule = p.vacation_rule || "flat";
      const base = p.vacation_days ?? 30;
      if (rule === "none") continue;
      if (rule === "prorated") {
        const h = trackedMsInRange(entries, oStart, oEnd, nowMs) / 3600000;
        total += base * h / FULLTIME_YEAR_HOURS;
      } else if (rule === "phase_total") {
        if (!p.end_date) {
          // Open-ended phase: its total length is unknown, so instead of inventing
          // one the whole amount is credited in the year the phase starts.
          if (p.start_date && pStart >= yStart && pStart < yEnd) total += base;
        } else {
          const phaseDays = (pEnd - pStart) / 86400000;
          if (phaseDays > 0) total += base * ((oEnd - oStart) / 86400000) / phaseDays;
        }
      } else {
        total += base * ((oEnd - oStart) / 86400000) / yearDays;
      }
    }
    return total;
  };
}

// ---- Canonical vacation balance (with carry-over) ---------------------------
// THE single source of truth for "offene Urlaubstage" in a calendar year.
//
// Carry-over from the previous year (`carryover.days`) is consumed FIRST by any
// vacation dated on or before its expiry (`carryover.expires_on`, ISO string or
// null = never expires). Whatever carry-over is left once the expiry date has
// passed is forfeited. Non-rejected, non-cancelled requests (approved + pending)
// count as used, half days as 0.5.
//
// A NEGATIVE carry-in is a debt from overbooking a previous year. It never
// expires; it is folded straight into the annual side (annualLeft), so it both
// shows in `open` and — via `annualRemaining` — rolls on into the next year
// until it is worked off.
export function vacationBalance(user, vacations, holidays, carryover, year, today = new Date(), entitlementFor = null) {
  const entitlement = entitlementFor ? entitlementFor(year) : (user?.vacation_days ?? 30);
  const carryDays = Number(carryover?.days || 0); // negative = debt carried forward
  const expiresOn = carryover?.expires_on ? String(carryover.expires_on).slice(0, 10) : null;
  const todayISO = formatISODate(today);

  // Enumerate the individual vacation days that fall in `year`, each with a date
  // and a weight (0.5 for a half day).
  const dayEvents = [];
  for (const v of vacations || []) {
    if (v.status === "rejected" || v.status === "cancelled") continue;
    const [sy, sm, sd] = v.start_date.split("-").map(Number);
    const [ey, em, ed] = v.end_date.split("-").map(Number);
    let cur = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    while (cur <= end) {
      if (cur.getFullYear() === year) {
        const wd = cur.getDay();
        const iso = formatISODate(cur);
        // Half weight for a single half day, or the last day of a half_end range.
        const weight = (v.half_day || (v.half_end && iso === v.end_date)) ? 0.5 : 1;
        if (wd !== 0 && wd !== 6 && !(holidays || {})[iso]) dayEvents.push({ iso, weight });
      }
      cur = addDays(cur, 1);
    }
  }
  dayEvents.sort((a, b) => a.iso.localeCompare(b.iso));

  // Deduct chronologically: days on/before expiry draw from carry-over first.
  // Debt (negative carry) starts on the annual side so it can never "expire".
  let carryLeft = Math.max(0, carryDays);
  let annualLeft = entitlement + Math.min(0, carryDays);
  let usedTotal = 0;
  for (const ev of dayEvents) {
    usedTotal += ev.weight;
    let w = ev.weight;
    if ((!expiresOn || ev.iso <= expiresOn) && carryLeft > 0) {
      const fromCarry = Math.min(w, carryLeft);
      carryLeft -= fromCarry;
      w -= fromCarry;
    }
    annualLeft -= w;
  }

  // Only an unused *surplus* expires.
  const carryExpired = carryDays > 0 && !!expiresOn && todayISO > expiresOn;
  const carryRemaining = carryExpired ? 0 : carryLeft;
  const round1 = (n) => Math.round(n * 10) / 10;
  return {
    entitlement: round1(entitlement),
    carryIn: round1(carryDays),
    carryRemaining: round1(carryRemaining),
    carryExpired,
    expiresOn,
    used: round1(usedTotal),
    // Rest des JAHRESANSPRUCHS — ohne Übertrag. Das ist die Zahl, die die UI
    // als "Offen" zeigt: Übertrag ist ein eigener Topf mit eigener Frist
    // (31.03.) und wird daneben separat ausgewiesen, statt vermischt zu werden.
    // Rollt außerdem ins Folgejahr weiter und darf negativ sein: mehr zu buchen
    // als verdient hinterlässt eine Schuld, statt zu verpuffen.
    annualRemaining: round1(annualLeft),
    // Jahresanspruch PLUS noch gültiger Übertrag — die Gesamtzahl der Tage, die
    // aktuell nehmbar wären. Die UI zeigt bewusst annualRemaining + Übertrag
    // getrennt; dieser Wert bleibt für Auswertungen/Tests erhalten.
    open: round1(annualLeft + carryRemaining),
  };
}

// Auto carry-over INTO `year` = the unused annual entitlement left at the end of
// the previous year, computed forward from the employment year (or the earliest
// year with data). Carry-over expires 31.03. of the year it lands in, so only the
// annual remainder — never last year's already-expired carry — rolls onward.
// (annualRemaining includes any inherited debt, so a negative balance keeps
// rolling forward year over year instead of vanishing after one hop.)
//
// Phase-aware: leftover vacation does NOT survive a change of employment phase.
// The chain therefore starts no earlier than the year in which the phase covering
// `year` began; anything left over from an earlier phase is forfeited.
export function computeCarryIn(user, vacations, holidays, year, today = new Date(), entitlementFor = null) {
  const employmentYear = user?.employed_since ? Number(String(user.employed_since).slice(0, 4)) : null;
  let firstYear = employmentYear ?? year;
  for (const v of vacations || []) {
    const y = Number(v.start_date.slice(0, 4));
    if (y < firstYear) firstYear = y;
  }
  const phases = user?.phases || [];
  if (phases.length > 0) {
    const yStart = `${year}-01-01`;
    const yEnd = `${year}-12-31`;
    let phaseStartYear = null;
    for (const p of phases) {
      if (!p.start_date || p.start_date > yEnd) continue;
      if (p.end_date && p.end_date <= yStart) continue; // stored end is EXCLUSIVE
      const sy = Number(String(p.start_date).slice(0, 4));
      if (phaseStartYear === null || sy > phaseStartYear) phaseStartYear = sy;
    }
    // No phase covers this year → no employment, nothing to carry in.
    if (phaseStartYear === null) return { days: 0, expires_on: `${year}-03-31` };
    if (phaseStartYear > firstYear) firstYear = phaseStartYear;
  }
  if (firstYear >= year) return { days: 0, expires_on: `${year}-03-31` };
  let carryDays = 0;
  for (let y = firstYear; y < year; y++) {
    const bal = vacationBalance(user, vacations, holidays, { days: carryDays, expires_on: `${y}-03-31` }, y, today, entitlementFor);
    carryDays = bal.annualRemaining; // what rolls into y+1 (debt included)
  }
  return { days: carryDays, expires_on: `${year}-03-31` };
}

// Sum of per-day Soll (dailyTargetMs) across the 7 days starting at `monday`.
// Respects fixed_workdays, half days and per-week overrides. Falls back to a flat
// weekly figure only when no user object is available (legacy callers).
export function weekTargetMs(user, monday, weeklyHoursFallback = 40) {
  if (!user) return weeklyHoursFallback * 3600000;
  let t = 0;
  for (let i = 0; i < 7; i++) t += dailyTargetMs(user, addDays(monday, i));
  return t;
}

// ---- Canonical Stundenkonto balance -----------------------------------------
// THE single source of truth for the +/- balance shown on the tile, in the
// Stundenkonto modal and in the admin overview. Sums tracked time (real entries
// plus synthetic holiday/vacation credit) minus per-day Soll across every
// completed week, from the first tracked week up to (excluding) the current week.
export function accountBalanceMs(user, realEntries, holidayEntries = [], vacationEntries = [], today = new Date(), nowMs = Date.now()) {
  const curMondayTs = getMonday(today).getTime();
  let firstMondayTs = Infinity;
  for (const e of realEntries) {
    const ts = getMonday(new Date(e.started_at)).getTime();
    if (ts >= curMondayTs) continue;
    if (ts < firstMondayTs) firstMondayTs = ts;
  }
  if (firstMondayTs === Infinity) return 0;
  const holSet = holidayDateSet(holidayEntries);
  let tracked = 0;
  for (const e of [...realEntries, ...holidayEntries, ...vacationEntries]) {
    const ts = getMonday(new Date(e.started_at)).getTime();
    if (ts < firstMondayTs || ts >= curMondayTs) continue;
    if (!countsAsTracked(e, holSet)) continue;
    tracked += entryDurationMs(e, nowMs);
  }
  let target = 0;
  const cursor = new Date(firstMondayTs);
  while (cursor.getTime() < curMondayTs) {
    target += dailyTargetMs(user, cursor);
    cursor.setDate(cursor.getDate() + 1);
  }
  return tracked - target;
}

// ---- Employment-phase scoping -----------------------------------------------
// Vacation is accounted PER PHASE: a Praktikum and a later Werkstudentenstelle are
// separate accounts and nothing carries between them. The self view (Zeit tab) and
// the admin detail view must scope a person identically, otherwise the same person
// sees two different Urlaubskonten — these helpers are that shared implementation.

// The phase a person is judged by: the one active on `today`, else the most recent.
export function activePhaseOf(user, today = new Date()) {
  const list = user?.phases || [];
  if (list.length === 0) return null;
  const iso = formatISODate(today);
  const sorted = [...list].sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)));
  // In a gap (or after the last phase) fall back to the most recent STARTED phase —
  // not simply the last one, which would be a job that hasn't begun yet.
  return sorted.find((p) => p.start_date <= iso && (!p.end_date || iso < p.end_date))
    || [...sorted].reverse().find((p) => p.start_date <= iso)
    || sorted[0];
}

// The person AS THEY WERE during `phase`. Narrowing `phases` to the single phase is
// what makes every Soll and every credit outside that window zero.
export function scopeUserToPhase(user, phase) {
  if (!user || !phase) return user;
  return {
    ...user,
    role: phase.type === "working_student" ? "working_student" : (user.role === "admin" ? "admin" : "employee"),
    weekly_hours: phase.weekly_hours,
    vacation_days: phase.vacation_days,
    fixed_workdays: phase.fixed_workdays || "",
    tracks_time: phase.tracks_time,
    employed_since: phase.start_date,
    employment_end: phase.end_date ? isoAddDays(phase.end_date, -1) : (user.employment_end || ""), // stored end is exclusive
    phases: [phase],
  };
}

// Time entries whose day falls inside the phase window [start, end).
export function scopeEntriesToPhase(entries, phase) {
  if (!phase) return entries || [];
  return (entries || []).filter((e) => {
    const iso = formatISODate(new Date(e.started_at));
    return iso >= phase.start_date && (!phase.end_date || iso < phase.end_date);
  });
}

// Vacation records CLIPPED to the phase window. A request that straddles the phase
// boundary keeps only its in-phase days, so days outside neither cost vacation days
// nor credit hours; records fully outside are dropped.
export function clipVacationsToPhase(vacations, phase) {
  if (!phase) return vacations || [];
  const lastISO = phase.end_date ? isoAddDays(phase.end_date, -1) : null;
  const out = [];
  for (const v of vacations || []) {
    if (v.end_date < phase.start_date) continue;
    if (lastISO && v.start_date > lastISO) continue;
    const start = v.start_date < phase.start_date ? phase.start_date : v.start_date;
    const end = lastISO && v.end_date > lastISO ? lastISO : v.end_date;
    if (end < start) continue;
    if (start === v.start_date && end === v.end_date) { out.push(v); continue; }
    // half_end marks the request's real LAST day — it only survives the clip when
    // that day is still inside the window.
    out.push({ ...v, start_date: start, end_date: end, half_end: v.half_end && end === v.end_date });
  }
  return out;
}

// THE single entry point for the Urlaubskonto numbers: scopes the person to one
// employment phase, clips their vacation records to it and returns the canonical
// balance for `year` plus the synthetic credit entries used to get there.
export function vacationAccountForPhase(user, phase, entries, vacations, holidays, year, today = new Date()) {
  const u = phase ? scopeUserToPhase(user, phase) : user;
  const vacs = phase ? clipVacationsToPhase(vacations, phase) : (vacations || []);
  const ents = phase ? scopeEntriesToPhase(entries, phase) : (entries || []);
  const holidayEntries = buildHolidayEntries(u, holidays);
  const vacationEntries = buildVacationEntries(u, vacs, holidays);
  const entitlementFor = entitlementForYear(u, [...ents, ...holidayEntries, ...vacationEntries]);
  const carry = computeCarryIn(u, vacs, holidays, year, today, entitlementFor);
  return {
    user: u,
    vacations: vacs,
    entries: ents,
    holidayEntries,
    vacationEntries,
    entitlementFor,
    carry,
    balance: vacationBalance(u, vacs, holidays, carry, year, today, entitlementFor),
  };
}

// ---- Vacation-day counting for list rows ------------------------------------

// Weekday-and-not-holiday days in [startISO, endISO], no half-day handling.
export function vacationDaysInRange(startISO, endISO, holidays) {
  if (!startISO || !endISO) return 0;
  const [sy, sm, sd] = startISO.split("-").map(Number);
  const [ey, em, ed] = endISO.split("-").map(Number);
  let cur = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  if (end < cur) return 0;
  let n = 0;
  while (cur <= end) {
    const wd = cur.getDay();
    if (wd !== 0 && wd !== 6 && !(holidays || {})[formatISODate(cur)]) n++;
    cur = addDays(cur, 1);
  }
  return n;
}

// True if the vacation record's real end date is a counted weekday (so a half_end
// flag actually removes half a day).
function endDayCounts(v, holidays) {
  const [y, m, d] = v.end_date.split("-").map(Number);
  const wd = new Date(y, m - 1, d).getDay();
  return wd !== 0 && wd !== 6 && !(holidays || {})[v.end_date];
}

// Counted vacation days of record `v` within [startISO, endISO] (usually the
// record's own range, possibly clipped), honouring half_day / half_end.
export function countedVacDays(startISO, endISO, holidays, v) {
  const n = vacationDaysInRange(startISO, endISO, holidays);
  if (v.half_day) return n * 0.5;
  if (v.half_end && endISO === v.end_date && endDayCounts(v, holidays)) return Math.max(0, n - 0.5);
  return n;
}
